import { Helmet } from "react-helmet-async";

const defaults = {
  siteName: "RADAR",
  siteUrl: "https://radar.aquivolabs.com",
  defaultTitle: "RADAR - Competitor Intelligence for Shopify",
  defaultDescription:
    "Monitor competitor ads across Meta, TikTok, and Google. Track pricing, stock changes, and storefront signals.",
  defaultOgImage: "https://radar.aquivolabs.com/logo.png",
  twitterHandle: "@radar_intel",
};

export function SEO({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage,
  noindex = false,
  article,
  structuredData,
}) {
  const pageTitle = title ? `${title} | ${defaults.siteName}` : defaults.defaultTitle;
  const pageDesc = description || defaults.defaultDescription;
  const pageUrl = canonical ? `${defaults.siteUrl}${canonical}` : defaults.siteUrl;
  const pageImage = ogImage || defaults.defaultOgImage;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <link rel="canonical" href={pageUrl} />
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : null}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content={defaults.siteName} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={defaults.twitterHandle} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />
      <meta name="twitter:image" content={pageImage} />
      {article ? <meta property="article:published_time" content={article.publishedTime} /> : null}
      {article?.author ? <meta property="article:author" content={article.author} /> : null}
      {article?.section ? <meta property="article:section" content={article.section} /> : null}
      {structuredData ? <script type="application/ld+json">{JSON.stringify(structuredData)}</script> : null}
    </Helmet>
  );
}

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "RADAR",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description: defaults.defaultDescription,
  url: defaults.siteUrl,
};

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.a,
    },
  })),
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: `${defaults.siteUrl}${item.href}`,
  })),
});

export const articleSchema = (article) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: article.title,
  description: article.excerpt,
  datePublished: article.datePublished,
  author: {
    "@type": "Organization",
    name: "RADAR",
  },
});
